import { Link } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import { advisorDomainChips } from '@/features/publicsite/registry/advisorDomainChips';
import { advisorSince } from '@/features/publicsite/registry/advisorSince';
import type { AdvisorCardProps } from '@/features/publicsite/types/registry/AdvisorCardProps';

/** An adviser's card on /consilieri: name, specialisations, zones, and the way to their pins. */
export function AdvisorCard({ advisor: a, onDomainSelect }: AdvisorCardProps) {
  const { t, language } = useTranslation(publicRegistry);
  const domains = advisorDomainChips(a, language);
  const since = advisorSince(a);
  const organization = a.organization?.trim();
  const email = a.contactEmail?.trim();
  const phone = a.contactPhone?.trim();
  return (
    // The organisation, when given, is the line under the name, and the specialisations follow it.
    <CatalogCard
      badge={t('badgePublic')}
      title={a.fullName}
      subtitle={organization || a.specializations}
    >
      {organization && a.specializations && <p className="muted">{a.specializations}</p>}
      {(email || phone) && (
        <p className="advisor-card__contact">
          {/* The address itself is in each name, so it still matches what is read (WCAG 2.5.3). */}
          {email && (
            <a
              href={`mailto:${email}`}
              aria-label={t('advisorsEmailAria', { name: a.fullName, email })}
            >
              {email}
            </a>
          )}
          {phone && (
            <a
              href={`tel:${phone.replace(/\s+/g, '')}`}
              aria-label={t('advisorsPhoneAria', { name: a.fullName, phone })}
            >
              {phone}
            </a>
          )}
        </p>
      )}
      {a.zoneNames && <p className="muted">{a.zoneNames}</p>}
      {/* What this adviser advises on (T243), in the same chips the coverage block uses. Nothing
          at all when there are none: an empty row would read as a domain the card failed to name.
          Named for a screen reader, which would otherwise meet a run of loose words after the
          raions with nothing saying what they are.

          Each one narrows the registry to that domain, by the id the chip already carries — the
          `domain` filter, not the search box. `q` is for someone typing and matches a substring
          of several fields; a chip is a choice the reader made from a closed list, and sending
          its name as text would answer it with everyone whose name happens to contain the word. */}
      {domains.length > 0 && (
        <ul className="chip-row advisor-card__domains" aria-label={t('advisorsDomainsAria')}>
          {domains.map((domain) => (
            <li key={domain.id}>
              <button
                type="button"
                className="chip"
                // Contains the visible text, so the accessible name does not replace it (WCAG 2.5.3).
                aria-label={t('advisorsDomainFilterBy', { domain: domain.label })}
                onClick={() => onDomainSelect(domain.id)}
              >
                {domain.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {since && <p className="muted">{t(since.key, { year: since.year ?? '' })}</p>}
      {/* Only when a geo point actually exists to jump to — AdvisorService#syncGeoPoints
          creates one per activity zone, so an adviser with none has nothing on the map to
          find, and this link would land on a search matching nothing. */}
      {a.zoneNames && (
        <p>
          <Link className="btn btn--ghost" to={`/harta?subjectId=${a.id}`}>
            <Icon name="map-pin" className="btn__icon" />
            {t('advisorsSeeOnMap')}
          </Link>
        </p>
      )}
    </CatalogCard>
  );
}
